<template>
  <div class="fade-in">
    <div class="columns">
      <div class="column is-full">
        <div class="is-pulled-left">
          <h1 class="title is-pulled-left">Virtual Visits</h1>
        </div>
        <div class="is-pulled-right">
          <button
            v-if="!isUser"
            class="button is-primary is-small is-pulled-right"
            @click="showScheduler"
          >
            <i class="fa fa-plus"></i> Add Visit
          </button>
        </div>
        <div class="is-clearfix"></div>
      </div>
    </div>

    <h3>Upcoming Appointments</h3>
    <table class="table is-striped is-responsive">
      <thead>
        <tr class="vmiddle">
          <col-sort
            sort="upcoming|name"
            :sorted="sorted.upcoming"
            :sort-dir="sortDir.upcoming"
            :click="sortClick"
            >Patient Name</col-sort
          >
          <col-sort
            sort="upcoming|date"
            :sorted="sorted.upcoming"
            :sort-dir="sortDir.upcoming"
            :click="sortClick"
            >Appointment Date</col-sort
          >
          <col-sort
            sort="upcoming|time"
            :sorted="sorted.upcoming"
            :sort-dir="sortDir.upcoming"
            :click="sortClick"
            >Appointment Time</col-sort
          >
          <col-sort
            sort="upcoming|provider"
            :sorted="sorted.upcoming"
            :sort-dir="sortDir.upcoming"
            :click="sortClick"
            >Provider</col-sort
          >
          <th>Reason For Visit</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-if="!loading.upcoming"
          v-for="encounter in Object.values(upcoming)"
        >
          <td>{{ patientName(encounter.relationships.patient.data.id) }}</td>
          <td>{{ encounter.attributes.appointmentAt | longDate }}</td>
          <td>
            {{ encounter.attributes.appointmentAt | time }}
            <span class="has-text-light"
              >[{{ encounter.attributes.duration }}]</span
            >
          </td>
          <td>{{ providerName(encounter.relationships.provider.data.id) }}</td>
          <td>{{ encounter.attributes.reasonForVisit }}</td>
          <td>
            <button
              v-if="!canJoin(encounter) && $can('encounter:update')"
              class="button is-primary"
              @click="editEncounter(encounter.id)"
            >
              Edit
            </button>
            <router-link
              :to="{ name: 'encounter-meet', params: { id: encounter.id } }"
              v-if="canJoin(encounter) && $can('encounter:meet')"
              class="button is-link"
            >
              Join Virtual Visit
            </router-link>
          </td>
        </tr>
        <tr v-if="loading.upcoming">
          <td colspan="6" class="has-text-centered">
            <i class="fa fa-refresh fa-spin fa-3x"></i>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="$can('encounter:sign')">
      <h3>Unsigned Visits</h3>
      <table class="table is-striped is-responsive table-accounts">
        <thead>
          <tr class="vmiddle">
            <col-sort
              sort="unsigned|name"
              :sorted="sorted.unsigned"
              :sort-dir="sortDir.unsigned"
              :click="sortClick"
              >Patient Name</col-sort
            >
            <col-sort
              sort="unsigned|date"
              :sorted="sorted.unsigned"
              :sort-dir="sortDir.unsigned"
              :click="sortClick"
              >Appointment Date</col-sort
            >
            <col-sort
              sort="unsigned|time"
              :sorted="sorted.unsigned"
              :sort-dir="sortDir.unsigned"
              :click="sortClick"
              >Appointment Time</col-sort
            >
            <col-sort
              sort="unsigned|provider"
              :sorted="sorted.unsigned"
              :sort-dir="sortDir.unsigned"
              :click="sortClick"
              >Provider</col-sort
            >
            <th>Reason For Visit</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-if="!loading.unsigned"
            v-for="encounter in Object.values(unsigned)"
          >
            <td>{{ patientName(encounter.relationships.patient.data.id) }}</td>
            <td>{{ encounter.attributes.appointmentAt | longDate }}</td>
            <td>{{ encounter.attributes.appointmentAt | time }}</td>
            <td>
              {{ providerName(encounter.relationships.provider.data.id) }}
            </td>
            <td>{{ encounter.attributes.reasonForVisit }}</td>
            <td>
              <router-link
                :to="{ name: 'encounter-meet', params: { id: encounter.id } }"
                v-if="$can('encounter:meet')"
                class="button is-primary"
              >
                Sign
              </router-link>
            </td>
          </tr>
          <tr v-if="loading.unsigned">
            <td colspan="6" class="has-text-centered">
              <i class="fa fa-refresh fa-spin fa-3x"></i>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <h3>Completed Appointments</h3>
    <table class="table is-striped is-responsive table-accounts">
      <thead>
        <tr class="vmiddle">
          <col-sort
            sort="completed|name"
            :sorted="sorted.completed"
            :sort-dir="sortDir.completed"
            :click="sortClick"
            >Patient Name</col-sort
          >
          <col-sort
            sort="completed|date"
            :sorted="sorted.completed"
            :sort-dir="sortDir.completed"
            :click="sortClick"
            >Appointment Date</col-sort
          >
          <col-sort
            sort="completed|time"
            :sorted="sorted.completed"
            :sort-dir="sortDir.completed"
            :click="sortClick"
            >Appointment Time</col-sort
          >
          <col-sort
            sort="completed|provider"
            :sorted="sorted.completed"
            :sort-dir="sortDir.completed"
            :click="sortClick"
            >Provider</col-sort
          >
          <th>Reason For Visit</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-if="!loading.completed"
          v-for="encounter in Object.values(completed)"
        >
          <td>{{ patientName(encounter.relationships.patient.data.id) }}</td>
          <td>{{ encounter.attributes.appointmentAt | longDate }}</td>
          <td>{{ encounter.attributes.appointmentAt | time }}</td>
          <td>{{ providerName(encounter.relationships.provider.data.id) }}</td>
          <td>{{ encounter.attributes.reasonForVisit }}</td>
          <td>
            <router-link
              :to="{ name: 'encounter-meet', params: { id: encounter.id } }"
              v-if="$can('encounter:meet')"
              class="button is-primary"
            >
              View
            </router-link>
          </td>
        </tr>
        <tr v-if="loading.completed">
          <td colspan="6" class="has-text-centered">
            <i class="fa fa-refresh fa-spin fa-3x"></i>
          </td>
        </tr>
      </tbody>
    </table>

    <vue-toastr ref="toastr"></vue-toastr>
    <schedule-create
      v-if="!isUser && !showEdit"
      @hide="hideModal"
      @added="reloadUpcoming"
    ></schedule-create>
    <schedule-edit
      v-if="!isUser && showEdit"
      @hide="hideModal"
      @updated="reloadUpcoming"
      :encounter="encounter"
    ></schedule-edit>
  </div>
</template>

<script>
import moment from "moment";
import EncountersService from "@/services/EncountersService";
import ColSort from "@/components/patient/ColSort";
import ScheduleCreate from "./ScheduleCreate";
import ScheduleEdit from "./ScheduleEdit";

export default {
  name: "encounter-index",

  components: {
    ScheduleCreate,
    ScheduleEdit,
    ColSort
  },

  data() {
    return {
      loading: {
        upcoming: false,
        unsigned: false,
        completed: false
      },
      sorted: {
        upcoming: "upcoming|date",
        unsigned: "unsigned|date",
        completed: "completed|date"
      },
      sortDir: {
        upcoming: "",
        unsigned: "",
        completed: "-"
      },
      encounter: {},
      showEdit: false
    };
  },

  computed: {
    upcoming() {
      return this.$store.getters["encounter/upcoming"];
    },
    unsigned() {
      return this.$store.getters["encounter/unsigned"];
    },
    completed() {
      return this.$store.getters["encounter/completed"];
    },
    isUser() {
      return this.$store.getters.isUser;
    }
  },

  methods: {
    patientName(id) {
      const patient = this.$store.getters["encounter/patient"](id);
      return `${patient.attributes.firstName} ${patient.attributes.lastName}`;
    },
    providerName(id) {
      const provider = this.$store.getters["encounter/provider"](id);
      return `${provider.attributes.firstName} ${provider.attributes.lastName}`;
    },
    sortClick(sortField) {
      const status = sortField.split("|")[0];
      if (this.sorted[status] === sortField) {
        if (this.sortDir[status] === "") {
          this.sortDir[status] = "-";
        } else {
          this.sortDir[status] = "";
        }
      } else {
        this.sorted[status] = sortField;
        this.sortDir[status] = "-";
      }
      this.load(status, false, 1);
    },
    load(status, pageNumber) {
      this.loading[status] = true;
      const params = {
        sortDir: this.sortDir[status],
        sorted: this.sorted[status].split("|")[1],
        status: EncountersService.status(status),
        pageNumber
      };
      EncountersService.load(params).then(() => {
        this.loading[status] = false;
      });
    },
    showScheduler() {
      this.$store.dispatch("encounter/toggleScheduler", true);
    },
    reloadUpcoming() {
      this.load("upcoming", 1);
      this.encounter = {};
      this.showEdit = false;
    },
    hideModal() {
      this.$store.dispatch("encounter/toggleScheduler", false);
      this.encounter = {};
      this.showEdit = false;
    },
    editEncounter(id) {
      const encounterData = this.$store.getters["encounter/getUpcoming"](id);
      this.encounter = {
        id,
        patient: {
          id: encounterData.relationships.patient.data.id,
          name: this.patientName(encounterData.relationships.patient.data.id)
        },
        date: moment(encounterData.attributes.appointmentAt).format(
          "MM/DD/YYYY"
        ),
        time: moment(encounterData.attributes.appointmentAt).format("hh:mm a"),
        duration: encounterData.attributes.duration,
        provider: {
          id: encounterData.relationships.provider.data.id,
          name: this.providerName(encounterData.relationships.provider.data.id)
        },
        reasonForVisit: encounterData.attributes.reasonForVisit
      };
      this.showEdit = true;
      this.$store.dispatch("encounter/toggleScheduler", true);
    },
    canJoin(encounter) {
      // only allow the join visit link to show, if the visit is less than 30 min away
      const now = moment();
      const visit = moment(encounter.attributes.appointmentAt);
      const minutesTill = moment.duration(visit.diff(now)).asMinutes();
      return (
        minutesTill > -1 * encounter.attributes.duration && minutesTill <= 30
      );
    }
  },

  mounted() {
    this.load("upcoming", 1);
    this.load("completed", 1);

    if (!this.isUser) {
      this.load("unsigned", 1);
    }
  }
};
</script>

<style lang="scss" scoped></style>
